import * as RD from '@radix-ui/react-dialog';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { haptics } from '../../lib/haptics';

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
}

// Threshold: 80px or 25% of the sheet's measured height, whichever is
// smaller. 80 is a comfortable thumb-pull on phone; 25% is the bail-out
// for short sheets where 80 would be the entire sheet.
const FIXED_THRESHOLD = 80;
// Above this downward velocity (px/ms) the sheet dismisses even if the
// raw distance is below threshold. Tuned to feel like a casual flick.
const VELOCITY_DISMISS = 0.5;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function BottomSheet({ open, onOpenChange, title, description, children }: BottomSheetProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  // Per-gesture mutable state. Lives in a ref so handlers don't trigger
  // a re-render on every pointermove.
  const gestureRef = useRef<{
    pointerId: number;
    startY: number;
    lastY: number;
    lastTime: number;
    velocity: number;
    threshold: number;
    reduced: boolean;
  } | null>(null);
  // Only this drives re-render: the live translateY while dragging.
  // 0 when not dragging or snapped back.
  const [dragY, setDragY] = useState(0);
  // When true, we suppress the CSS transition so the sheet follows the
  // finger 1:1. When false (snap-back), the transition is re-enabled
  // so the spring back to 0 is smooth.
  const [dragging, setDragging] = useState(false);

  const reset = useCallback(() => {
    gestureRef.current = null;
    setDragging(false);
    setDragY(0);
  }, []);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // Ignore non-primary mouse buttons. Touch/pen always pass.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const contentEl = contentRef.current;
    // Threshold: min(80, 25% of sheet height). offsetHeight is fine —
    // this read is synchronous with the gesture start and the value
    // doesn't need to react to resize during a one-shot drag.
    const sheetH = contentEl?.offsetHeight ?? 0;
    const threshold = Math.min(FIXED_THRESHOLD, Math.max(40, sheetH * 0.25));
    gestureRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      lastY: e.clientY,
      lastTime: e.timeStamp,
      velocity: 0,
      threshold,
      reduced: prefersReducedMotion(),
    };
    // Capture so we keep receiving move/up even if the finger leaves
    // the handle (which is small).
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDragging(true);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    const dy = e.clientY - g.startY;
    // Track velocity from the most recent two samples. dt can be 0 on
    // back-to-back events; guard against div-by-zero by falling through.
    const dt = e.timeStamp - g.lastTime;
    if (dt > 0) {
      g.velocity = (e.clientY - g.lastY) / dt;
    }
    g.lastY = e.clientY;
    g.lastTime = e.timeStamp;
    // Clamp upward drag at 0 — the sheet should not lift past its
    // resting position.
    const clamped = Math.max(0, dy);
    // prefers-reduced-motion: still track for dismiss decision, but
    // don't push a translate to the DOM. Matches usePullToRefresh's
    // pattern: gesture survives, in-progress visual is suppressed.
    if (g.reduced) return;
    setDragY(clamped);
  }

  function onPointerEnd(e: React.PointerEvent<HTMLDivElement>) {
    const g = gestureRef.current;
    if (!g || e.pointerId !== g.pointerId) return;
    const dy = Math.max(0, e.clientY - g.startY);
    const flick = g.velocity > VELOCITY_DISMISS;
    const crossed = dy >= g.threshold;
    if (crossed || flick) {
      // Hand control back to Radix's close animation. Clearing the
      // drag offset first prevents a flash of "stuck partway down."
      reset();
      haptics.warn();
      onOpenChange(false);
      return;
    }
    // Below threshold: snap back. Re-enable the CSS transition for a
    // smooth return, then clear the offset on the next paint.
    gestureRef.current = null;
    setDragging(false);
    setDragY(0);
  }

  // Inline style on the content element. We use translate3d to nudge
  // the browser onto the compositor; the existing sheet-in keyframe
  // animation already uses transform, so there's no conflict — Radix
  // only runs that on data-state change, not while open.
  const contentStyle: React.CSSProperties =
    dragY > 0
      ? {
          transform: `translate3d(0, ${dragY}px, 0)`,
          transition: dragging ? 'none' : undefined,
        }
      : dragging
        ? { transition: 'none' }
        : {};

  return (
    <RD.Root open={open} onOpenChange={onOpenChange}>
      <RD.Portal>
        <RD.Overlay className="dialog-overlay" />
        <RD.Content
          ref={contentRef}
          className="dialog-content"
          aria-describedby={description ? 'sheet-desc' : undefined}
          style={contentStyle}
        >
          {/* Pointer-event surface scoped to the handle only. A scrollable
              body inside the sheet (e.g. LogDoseSheet's long form) must
              not trigger drag-to-dismiss when the user is mid-scroll —
              the contract is "drag from the handle to dismiss." */}
          <div
            className="dialog-handle"
            aria-hidden="true"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
            style={{ touchAction: 'none', cursor: 'grab' }}
          />
          <RD.Title className="dialog-title">{title}</RD.Title>
          {description ? (
            <RD.Description id="sheet-desc" className="muted" style={{ marginBlockStart: 'var(--space-2)' }}>
              {description}
            </RD.Description>
          ) : null}
          <div style={{ marginBlockStart: 'var(--space-4)' }}>{children}</div>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
